import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { buildQueryParamsString } from "../utils/url.ts";
import { checkVersionCompatibility } from "./checkVersionCompatibility.ts";

export const useAppQuery = <T>({
  queryKey,
  queryParamsKey,
  onSuccess,
  onError,
  enabled = true,
  staleTime = Infinity,
}: {
  queryKey: string | null /* null implies enabled: false */;
  queryParamsKey?: { [x: string]: string | string[] };
  enabled?: boolean;
  staleTime?: number;
  onSuccess?: (response: T) => void;
  onError?: () => void;
}) => {
  const { getAccessTokenSilently } = useAuth0();

  let queryUrl = queryKey ? `/api/${queryKey}` : null;
  if (queryUrl && queryParamsKey) {
    queryUrl = `${queryUrl}?${buildQueryParamsString(queryParamsKey)}`;
  }

  return useQuery<T>({
    // Our invalidations are supposed to ensure that data is never stale
    // so we do not need to make it stale artificially with a timer.
    staleTime,
    queryKey: queryParamsKey ? [queryKey, queryParamsKey] : [queryKey],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { data, headers } = await axios.get(queryUrl!, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      checkVersionCompatibility(headers);
      return data;
    },
    onSuccess,
    onError,
    retry: false,
    enabled: queryKey ? enabled : false,
  });
};
